import { useEffect, useState } from "react";
import { usePageSEO } from "../hooks/usePageSEO";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Check, Sparkles, ChevronLeft, ArrowRight, ChevronDown,
  MessageSquare, Star, ExternalLink, Home, TrendingUp,
} from "lucide-react";
import { LOGO_URL } from "../lib/brand";
import { log } from "../lib/log";
import { BACKEND_URL } from "../lib/apiBase";

const API = `${BACKEND_URL}/api`;

const FAQS = [
  {
    q: "Wie viel sollte ich einzahlen?",
    a: "Das ist sehr individuell und hängt von deiner finanziellen Situation, deinen Zielen und deiner Risikobereitschaft ab. Wir haben das Minimum bewusst tief mit 2.000 USD angesetzt, damit du das System mit überschaubarem Kapital ausprobieren kannst. Frag deinen Empfehlungsgeber nach einer Empfehlung, die zu dir passt.",
  },
  {
    q: "Was passiert in einem Verlustmonat?",
    a: "Verlustmonate gehören dazu — das ist bei jedem ehrlichen Trading-System so. Wichtig: Unser Risiko-Management ist hart im Code limitiert, ein Daily-Loss-Limit stoppt das System bei zu starkem Drawdown automatisch. So bleiben Verlustmonate kontrollierbar und das Konto erholt sich strategisch.",
  },
  {
    q: "Kann ich jederzeit aussteigen?",
    a: "Ja, jederzeit. Es gibt keine Mindestlaufzeit, keine Kündigungsfristen, keine Ausstiegsgebühr. Du bist über deinen eigenen Broker-Account angemeldet — das Kapital bleibt zu 100 % auf deinem Namen und du kannst jederzeit auszahlen oder das Copy-Trading deaktivieren.",
  },
  {
    q: "Was kostet das wirklich?",
    a: "Keine Fixkosten. Keine Setup-Gebühr. Keine Monatspauschale. Wir verdienen ausschliesslich über eine Performance Fee — also nur dann, wenn wir auch für dich Gewinn erwirtschaften. Das ist die ehrlichste Art der Bezahlung, weil unsere Interessen und deine identisch sind.",
  },
  {
    q: "Wie lange dauert das Onboarding?",
    a: "Wenn du dich konzentriert hinsetzt, bist du in etwa 30 Minuten startklar — RoboForex-Account eröffnen, Geld einzahlen, Copy-Trading verbinden. Wir führen dich Schritt für Schritt durch den Prozess mit Videos und einer abhakbaren Checkliste. Bei Fragen meldest du dich einfach bei deinem Empfehlungsgeber.",
  },
];

function fmt(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(2)}%`;
}

export default function WebinarFinish() {
  const [rows, setRows] = useState([]);
  const [openFaq, setOpenFaq] = useState(0);

  usePageSEO({ noIndex: true });

  useEffect(() => {
    document.title = "Danke fürs Dabeisein · Alphalane Launch-Call";
    (async () => {
      try {
        const r = await axios.get(`${API}/track-record`);
        setRows(Array.isArray(r.data) ? r.data : []);
      } catch (err) {
        log.warn("[webinar-finish] track-record fetch failed:", err);
      }
    })();
  }, []);

  // Compute summary stats
  const stats = (() => {
    if (!rows.length) return null;
    const monthly = rows.map((r) => r.monthly).filter((v) => typeof v === "number");
    const best = monthly.length ? Math.max(...monthly) : 0;
    const worst = monthly.length ? Math.min(...monthly) : 0;
    const cumulative = rows[rows.length - 1]?.cumulative ?? 0;
    return { best, worst, cumulative, count: rows.length };
  })();

  const recentRows = rows.slice(-6);

  return (
    <div className="min-h-screen bg-[var(--al-bg)]" data-testid="webinar-finish-page">
      <header className="border-b border-[var(--al-line)] bg-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 group">
            <img src={LOGO_URL} alt="Alphalane" className="h-9 w-auto" />
            <span className="hidden sm:inline font-display font-bold text-base text-slate-900 group-hover:text-[var(--al-purple)] transition-colors">
              Alphalane
            </span>
          </Link>
          <Link
            to="/"
            data-testid="finish-back-home"
            className="text-xs sm:text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
          >
            <ChevronLeft className="h-4 w-4" /> Zur Startseite
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        {/* Hero */}
        <section className="text-center mb-14" data-testid="finish-hero">
          <div className="inline-flex h-20 w-20 rounded-full al-grad-bg items-center justify-center shadow-2xl shadow-violet-200 mb-6">
            <Check className="h-10 w-10 text-white" strokeWidth={3} />
          </div>
          <span className="al-tag justify-self-center">
            <Sparkles className="h-3 w-3" /> Webinar beendet
          </span>
          <h1 className="mt-4 font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            Danke fürs <span className="al-grad-text">Dabeisein.</span>
          </h1>
          <p className="mt-5 text-lg sm:text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto">
            Schön, dass du dir die Zeit genommen hast. Hier ist alles, was du
            jetzt für deine Entscheidung brauchst — und der nächste Schritt für dich.
          </p>
        </section>

        {/* Next step */}
        <section className="mb-14 al-card p-7 sm:p-10 bg-gradient-to-br from-violet-50/60 to-white border-violet-200" data-testid="finish-next-step">
          <div className="flex items-start gap-5">
            <div className="flex-shrink-0 h-14 w-14 rounded-2xl al-grad-bg flex items-center justify-center text-white font-display font-bold text-2xl shadow-lg shadow-violet-200">
              1
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] uppercase tracking-widest font-bold text-[var(--al-purple)] mb-1">
                Dein nächster Schritt
              </div>
              <h2 className="font-display font-bold text-2xl sm:text-3xl leading-tight tracking-tight">
                Melde dich bei der Person, die dich eingeladen hat.
              </h2>
              <p className="mt-4 text-[15px] sm:text-base text-slate-700 leading-relaxed">
                Sie kennt dich, dein Umfeld und kann dir die letzten Fragen persönlich beantworten —
                ehrlich und auf deine Situation zugeschnitten. Sag ihr Bescheid, dass du das Webinar
                gesehen hast und besprich gemeinsam mit ihr, ob und wie ein Einstieg für dich Sinn ergibt.
              </p>
              <p className="mt-3 text-sm text-slate-500 italic">
                Es gibt keinen Verkaufsdruck — wir freuen uns einfach über jede Antwort, ob ja oder nein.
              </p>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="mb-14" data-testid="finish-faqs">
          <div className="text-center mb-8">
            <span className="al-tag justify-self-center">
              <MessageSquare className="h-3 w-3" /> Häufigste Fragen aus dem Webinar
            </span>
            <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl leading-tight tracking-tight">
              Was im Q&amp;A gefragt wurde.
            </h2>
            <p className="mt-3 text-slate-600 max-w-xl mx-auto">
              Die wichtigsten Antworten nochmal in Ruhe zum Nachlesen.
            </p>
          </div>
          <div className="al-card overflow-hidden">
            {FAQS.map((f, i) => {
              const isOpen = openFaq === i;
              return (
                <div
                  key={f.q}
                  data-testid={`finish-faq-${i}`}
                  className={`border-b border-[var(--al-line)] last:border-b-0 ${isOpen ? "bg-violet-50/30" : ""}`}
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? -1 : i)}
                    data-testid={`finish-faq-toggle-${i}`}
                    className="w-full px-5 sm:px-7 py-5 flex items-center justify-between gap-4 text-left hover:bg-slate-50 transition-colors"
                  >
                    <span className={`font-display font-bold text-base sm:text-lg leading-tight ${isOpen ? "text-[var(--al-purple)]" : "text-slate-900"}`}>
                      {f.q}
                    </span>
                    <ChevronDown className={`h-5 w-5 text-slate-400 flex-shrink-0 transition-transform ${isOpen ? "rotate-180 text-[var(--al-purple)]" : ""}`} />
                  </button>
                  {isOpen && (
                    <div className="px-5 sm:px-7 pb-6 text-[15px] text-slate-700 leading-relaxed">
                      {f.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Track Record */}
        <section className="mb-14 al-card p-6 sm:p-8" data-testid="finish-trackrecord">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="h-4 w-4 text-[var(--al-purple)]" />
            <h2 className="font-display font-bold text-lg">Track Record im Überblick</h2>
          </div>
          <p className="text-sm text-slate-600 mb-5">
            Performance seit Live-Start. Tagesaktuell aus der Datenbank.
          </p>

          {stats && (
            <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6">
              <Stat label="Kumuliert" value={fmt(stats.cumulative)} positive={stats.cumulative >= 0} />
              <Stat label="Bester Monat" value={fmt(stats.best)} positive={stats.best >= 0} />
              <Stat label="Schwächster Monat" value={fmt(stats.worst)} positive={stats.worst >= 0} />
            </div>
          )}

          {recentRows.length > 0 && (
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-widest font-bold text-slate-500">Monat</th>
                    <th className="text-right px-4 py-2.5 text-[11px] uppercase tracking-widest font-bold text-slate-500">Monatlich</th>
                    <th className="text-right px-4 py-2.5 text-[11px] uppercase tracking-widest font-bold text-slate-500">Kumuliert</th>
                  </tr>
                </thead>
                <tbody>
                  {recentRows.map((r, i) => (
                    <tr key={r.month || `tr-${i}`} className="border-t border-slate-100">
                      <td className="px-4 py-2.5 font-display font-semibold text-slate-800">{r.month}</td>
                      <td className={`px-4 py-2.5 text-right font-mono-tab font-bold ${r.monthly >= 0 ? "text-emerald-600" : "text-rose-500"}`}>{fmt(r.monthly)}</td>
                      <td className="px-4 py-2.5 text-right font-mono-tab text-slate-700">{fmt(r.cumulative)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-5 text-center">
            <Link to="/#track-record" data-testid="finish-tr-full" className="al-cta-ghost text-sm">
              Vollständigen Track Record ansehen <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>

        {/* Trustpilot */}
        <section className="mb-14 al-card p-7 sm:p-9 text-center" data-testid="finish-trustpilot">
          <div className="inline-flex items-center gap-1.5 mb-4">
            {[0, 1, 2, 3, 4].map((i) => (
              <Star key={i} className="h-7 w-7 text-emerald-500 fill-emerald-500" strokeWidth={0} />
            ))}
          </div>
          <h2 className="font-display font-bold text-2xl sm:text-3xl leading-tight tracking-tight mb-3">
            Was andere über uns sagen.
          </h2>
          <p className="text-slate-600 leading-relaxed max-w-xl mx-auto mb-6">
            Echte Bewertungen von echten Menschen auf Trustpilot — komplett unzensiert
            und unabhängig.
          </p>
          <a
            href="https://ch.trustpilot.com/review/moonshine-solutions.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="finish-trustpilot-link"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-display font-bold text-base shadow-lg shadow-emerald-100 transition-colors"
          >
            Bewertungen auf Trustpilot ansehen <ExternalLink className="h-4 w-4" />
          </a>
        </section>

        {/* Back to homepage */}
        <section className="al-card p-7 sm:p-9 text-center bg-gradient-to-br from-white to-violet-50/30" data-testid="finish-home">
          <div className="inline-flex h-12 w-12 rounded-xl al-grad-bg items-center justify-center mb-4">
            <Home className="h-5 w-5 text-white" />
          </div>
          <h2 className="font-display font-bold text-xl sm:text-2xl leading-tight tracking-tight mb-2">
            Mehr Infos auf einen Blick.
          </h2>
          <p className="text-slate-600 mb-5 max-w-lg mx-auto text-[15px]">
            Auf unserer Startseite findest du alle Details zum System, dem Team und unserem Ansatz.
          </p>
          <Link to="/" data-testid="finish-home-link" className="al-cta text-sm">
            Zur Startseite <ArrowRight className="h-4 w-4" />
          </Link>
        </section>
      </main>

      <footer className="border-t border-[var(--al-line)] mt-12">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-6 text-[11px] text-slate-400 flex flex-wrap gap-4 items-center justify-between">
          <div>© Alphalane Trading Systems · Moonshine Solutions GmbH</div>
          <div className="flex gap-4">
            <Link to="/impressum" className="hover:text-slate-700">Impressum</Link>
            <Link to="/datenschutz" className="hover:text-slate-700">Datenschutz</Link>
            <Link to="/agb" className="hover:text-slate-700">AGB</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

const Stat = ({ label, value, positive }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4 text-center">
    <div className="text-[10px] sm:text-[11px] uppercase tracking-widest font-bold text-slate-400 mb-1">
      {label}
    </div>
    <div className={`font-mono-tab font-bold text-lg sm:text-2xl ${positive ? "text-emerald-600" : "text-rose-500"}`}>
      {value}
    </div>
  </div>
);
